import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, BarChart3, LayoutDashboard,
  Mail, CheckCircle2,
  ArrowRight, Play, Clock, ChevronDown,
  Menu, X, AlertTriangle, Scale, Building2, Hammer, Send,
  Phone, ChevronLeft, ChevronRight, Shield, Zap,
  ClipboardList, FolderOpen, TrendingUp, Gavel, BookOpen, Wrench, Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  modules, benefits, steps, pricingPlans, heroSlides, faqs,
  comingSoonItems, careerRoles
} from '@/components/landing/landing-data';
import { ComingSoonSection } from '@/components/landing/ComingSoonSection';
import { ProductTour } from '@/components/landing/ProductTour';
import { TrustBar } from '@/components/landing/TrustBar';
import { AnimatedCounter } from '@/components/landing/AnimatedCounter';

import { api } from '@/api';

const CACHE_BUST = '?v=20260404a';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const isLoggedIn = !!user;

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000); // Change slide every 6 seconds
    
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const prevSlide = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const goToSlide = useCallback((index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post('/contact', contactForm);
      toast.success('Thank you for your message! We\'ll be in touch soon.');
      setContactForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/gantt-logo-white.png" 
                alt="Gantt ACC" 
                className="h-8 w-auto"
              />
              <span className="hidden lg:block text-[11px] tracking-[0.2em] uppercase text-slate-400 font-medium border-l border-slate-700 pl-3">
                Artificial Construction Cloud
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
                Features
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
                How It Works
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
                Pricing
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-white transition-colors text-sm font-medium">
                Contact
              </button>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-white hover:bg-slate-100 text-slate-900 font-semibold"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-slate-300 hover:text-white hover:bg-slate-800"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => navigate('/login')}
                    className="bg-white hover:bg-slate-100 text-slate-900 font-semibold"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 px-4 py-4 space-y-3">
            <button onClick={() => scrollToSection('features')} className="block w-full text-left text-slate-300 hover:text-white py-2">Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className="block w-full text-left text-slate-300 hover:text-white py-2">How It Works</button>
            <button onClick={() => scrollToSection('pricing')} className="block w-full text-left text-slate-300 hover:text-white py-2">Pricing</button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-slate-300 hover:text-white py-2">Contact</button>
            <div className="pt-3 border-t border-slate-800 space-y-2">
              {isLoggedIn ? (
                <Button onClick={() => navigate('/dashboard')} className="w-full bg-white hover:bg-slate-100 text-slate-900">
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate('/login')} className="w-full border-slate-600 text-white hover:bg-slate-800">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/login')} className="w-full bg-white hover:bg-slate-100 text-slate-900">
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Sliding Carousel */}
      <section className="relative pt-16 md:pt-20 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 overflow-x-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-slate-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          {/* Slide Content */}
          <div className="relative">
            {heroSlides.map((slide, index) => {
              const IconComponent = slide.badgeIcon;
              return (
                <div
                  key={index}
                  className={`transition-all duration-700 ease-in-out ${
                    index === currentSlide 
                      ? 'opacity-100 translate-x-0' 
                      : index < currentSlide 
                        ? 'opacity-0 -translate-x-full absolute inset-0' 
                        : 'opacity-0 translate-x-full absolute inset-0'
                  }`}
                >
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
                    {/* Left - Text Content */}
                    <div className="text-left">
                      {/* Badge */}
                      <div className="inline-flex items-center gap-2 bg-slate-800/80 backdrop-blur border border-slate-700 rounded-full px-3 py-1 mb-4">
                        <IconComponent className="h-3.5 w-3.5 text-cyan-400" />
                        <span className="text-slate-300 text-xs font-medium">{slide.badge}</span>
                      </div>

                      {/* Headline */}
                      <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
                        {slide.headline}<br />
                        <span className="bg-gradient-to-r from-cyan-400 to-slate-400 bg-clip-text text-transparent">
                          {slide.highlightedText}
                        </span>
                        {slide.afterText && <><br />{slide.afterText}</>}
                      </h1>

                      {/* Description */}
                      <p className="text-base md:text-lg text-slate-400 max-w-xl mb-6">
                        {slide.description}
                      </p>

                      {/* CTA Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        {isLoggedIn ? (
                          <Button 
                            onClick={() => navigate('/dashboard')}
                            className="bg-white hover:bg-slate-100 text-slate-900 font-bold px-6 py-2.5 h-auto shadow-lg shadow-white/10"
                          >
                            Go to Dashboard
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        ) : (
                          <>
                            <Button 
                              onClick={() => navigate('/login')}
                              className="bg-white hover:bg-slate-100 text-slate-900 font-bold px-6 py-2.5 h-auto shadow-lg shadow-white/10"
                            >
                              Get Started
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => scrollToSection('product-tour')}
                              className="border-slate-600 text-white hover:bg-slate-800 font-bold px-6 py-2.5 h-auto"
                            >
                              <Play className="mr-2 h-4 w-4" />
                              Take the Tour
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Stats for this slide */}
                      <div className="flex gap-6">
                        {slide.stats.map((stat, i) => (
                          <div key={i} className="text-left">
                            <div className="text-xl md:text-2xl font-black text-white">{stat.value}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right - Screenshot */}
                    <div className="relative hidden lg:block">
                      <div className="relative">
                        {/* Glow effect */}
                        <div className="absolute -inset-3 bg-gradient-to-r from-cyan-500/20 to-slate-500/20 rounded-2xl blur-2xl" />
                        
                        {/* Screenshot container */}
                        <div className="relative rounded-xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/50">
                          <img 
                            src={slide.image} 
                            alt="Platform Screenshot" 
                            className="w-full h-auto"
                          />
                          {/* Overlay gradient */}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Slider Controls */}
          <div className="flex items-center justify-between mt-8">
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="p-2.5 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-8 h-2.5 bg-cyan-400 rounded-full' 
                      : 'w-2.5 h-2.5 bg-slate-600 rounded-full hover:bg-slate-500'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={nextSlide}
              className="p-2.5 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 h-0.5 bg-slate-800 rounded-full overflow-hidden max-w-md mx-auto">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-slate-400 transition-all duration-300"
              style={{ 
                width: `${((currentSlide + 1) / heroSlides.length) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="pb-4 flex justify-center">
          <ChevronDown className="h-6 w-6 text-slate-500 animate-bounce" />
        </div>
      </section>

      {/* Trust Bar */}
      <TrustBar />

      {/* Interactive Product Tour */}
      <ProductTour />

      {/* Problem Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              The Challenges Contractors Face
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Construction delays are inevitable. Missing documentation deadlines shouldn't be.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-5">
                <AlertTriangle className="h-8 w-8 text-red-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Missed Time-Bar Deadlines</h3>
                <p className="text-sm text-slate-600">
                  JBCC requires 20 working days, FIDIC gives 28 calendar days, and NEC allows 8 weeks. Miss the deadline and lose your entitlement forever.
                </p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-5">
                <FileText className="h-8 w-8 text-amber-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Complex Documentation</h3>
                <p className="text-sm text-slate-600">
                  Creating legally compliant notices and claims takes hours of specialist work. Most contractors don't have the in-house expertise.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 bg-slate-50/50">
              <CardContent className="p-5">
                <Scale className="h-8 w-8 text-slate-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Lost Revenue from Rejected Claims</h3>
                <p className="text-sm text-slate-600">
                  Poor documentation leads to rejected claims. South African contractors lose millions in legitimate entitlements every year.
                </p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-5">
                <Mail className="h-8 w-8 text-blue-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">RFIs Lost in Email Chains</h3>
                <p className="text-sm text-slate-600">
                  Critical information requests get buried in inboxes. Unanswered RFIs cause costly site delays and finger-pointing when nobody can find who asked what, when.
                </p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-5">
                <ClipboardList className="h-8 w-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Meeting Actions Never Tracked</h3>
                <p className="text-sm text-slate-600">
                  Minutes get distributed but action items are never followed up. The same issues resurface week after week, wasting everyone's time.
                </p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-5">
                <FolderOpen className="h-8 w-8 text-purple-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Shop Drawings Stuck in Review</h3>
                <p className="text-sm text-slate-600">
                  Submissions pile up with consultants. Nobody knows which trade is blocked, what revision we're on, or who's been waiting the longest.
                </p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-5">
                <TrendingUp className="h-8 w-8 text-emerald-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">No Visibility on Variations</h3>
                <p className="text-sm text-slate-600">
                  Variation orders are tracked in scattered spreadsheets. Approved amounts don't reconcile, and the commercial position is always a guess.
                </p>
              </CardContent>
            </Card>
            <Card className="border-rose-200 bg-rose-50/50">
              <CardContent className="p-5">
                <Shield className="h-8 w-8 text-rose-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Chaotic Close-Out Handovers</h3>
                <p className="text-sm text-slate-600">
                  Outstanding defects, missing documentation, unsigned warranties — close-out drags on for months because there's no single system tracking it all.
                </p>
              </CardContent>
            </Card>
            <Card className="border-indigo-200 bg-indigo-50/50">
              <CardContent className="p-5">
                <Gavel className="h-8 w-8 text-indigo-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Disputes Escalate Without Records</h3>
                <p className="text-sm text-slate-600">
                  By the time a matter reaches adjudication, critical correspondence is missing, timelines are unclear, and evidence hasn't been properly compiled.
                </p>
              </CardContent>
            </Card>
            <Card className="border-teal-200 bg-teal-50/50">
              <CardContent className="p-5">
                <BookOpen className="h-8 w-8 text-teal-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Daily Site Diaries Are Incomplete</h3>
                <p className="text-sm text-slate-600">
                  Site agents rush through diary entries or skip them entirely. Weather, labour, plant breakdowns, and delays aren't captured — leaving gaps in claim evidence.
                </p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-5">
                <Wrench className="h-8 w-8 text-orange-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">Plant Breakdowns Go Unrecorded</h3>
                <p className="text-sm text-slate-600">
                  A crane breaks down for half a day and nobody logs it. When you need to substantiate an Extension of Time claim, there's no evidence of equipment downtime.
                </p>
              </CardContent>
            </Card>
            <Card className="border-cyan-200 bg-cyan-50/50">
              <CardContent className="p-5">
                <Users className="h-8 w-8 text-cyan-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">New Staff Don't Know the System</h3>
                <p className="text-sm text-slate-600">
                  Every time a new QS, site agent, or administrator joins the project, they take weeks to learn the processes. No structured onboarding, no help documentation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features/Modules Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Everything You Need to Manage Projects
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive suite of tools designed specifically for construction project management and delay claim administration.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <Card 
                key={index} 
                className={`group hover:shadow-lg transition-all border-slate-200 cursor-pointer ${expandedModule === index ? 'ring-2 ring-slate-900' : ''}`}
                onClick={() => setExpandedModule(expandedModule === index ? null : index)}
              >
                <CardContent className="p-6">
                  <div className={`inline-flex p-3 rounded-lg ${module.bgColor} mb-4`}>
                    <module.icon className={`h-6 w-6 ${module.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-slate-700 transition-colors">
                    {module.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-3">
                    {module.description}
                  </p>
                  
                  {/* Expanded details */}
                  {expandedModule === index && (
                    <ul className="space-y-2 mt-4 pt-4 border-t border-slate-200">
                      {module.details.map((detail, dIndex) => (
                        <li key={dIndex} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <div className="flex items-center gap-1 text-xs text-slate-400 mt-3">
                    <span>{expandedModule === index ? 'Click to collapse' : 'Click for details'}</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${expandedModule === index ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <ComingSoonSection items={comingSoonItems} onScrollToSection={scrollToSection} />

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              How It Works
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              From project setup to portfolio-level oversight in six steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-800 border-2 border-slate-600 text-white font-black text-xl mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Why Contractors Choose Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real results for construction professionals across South Africa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex p-4 rounded-full bg-slate-100 mb-4">
                  <benefit.icon className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Simple, Per-Project Pricing
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Pay based on your project value. All features included at every tier.
            </p>
          </div>

          <Card className="border-slate-200 overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="text-left py-4 px-6 font-bold">Project Value</th>
                    <th className="text-right py-4 px-6 font-bold">Price per Month</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingPlans.map((plan, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-slate-200 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100 transition-colors`}
                    >
                      <td className="py-4 px-6">
                        <span className="font-semibold text-slate-900">{plan.tier}</span>
                        <span className="block text-sm text-slate-500">{plan.description}</span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <span className="text-2xl font-black text-slate-900">{plan.price}</span>
                        <span className="text-sm text-slate-500 ml-1">/month</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-6">
              All tiers include: AI Notice & Claim Generation, IRS & Timing Schedule, Programme Analysis, 
              Professional PDFs, Dashboard & Reporting, Unlimited Users, Email Support
            </p>
            <Button 
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-8"
              onClick={() => scrollToSection('contact')}
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card 
                key={index} 
                className={`border-slate-200 cursor-pointer transition-all ${openFaq === index ? 'shadow-md' : ''}`}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-slate-900 pr-4">{faq.question}</h3>
                    <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                  </div>
                  {openFaq === index && (
                    <p className="mt-4 text-slate-600">{faq.answer}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
                Get In Touch
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Have questions about how Gantt can help your construction business? 
                We'd love to hear from you. Fill out the form and our team will get back to you within 24 hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-slate-200 rounded-lg">
                    <Mail className="h-6 w-6 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Email</h3>
                    <p className="text-slate-600">info@gantt.co.za</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-slate-200 rounded-lg">
                    <Phone className="h-6 w-6 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Phone</h3>
                    <p className="text-slate-600">073 049 1360</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-slate-200">
              <CardContent className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <Input 
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        placeholder="Your name"
                        className="border-slate-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <Input 
                        required
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="your@email.com"
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                      <Input 
                        value={contactForm.company}
                        onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                        placeholder="Your company"
                        className="border-slate-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <Input 
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        placeholder="+27 ..."
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                    <Textarea 
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Tell us about your project and how we can help..."
                      rows={5}
                      className="border-slate-300"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-bold tracking-widest text-cyan-600 uppercase mb-3">About Gantt ACC</span>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
                The Artificial Construction Cloud
              </h2>
              <p className="text-slate-500 text-sm font-semibold mb-4 tracking-wide">Built by Planners, for the Industry</p>
              <p className="text-slate-600 mb-6">
                Gantt wasn't built in a boardroom — it was born on construction sites. After 15 years of compiling baseline programmes, tracking delay events, and preparing EOT claims by hand, we knew there had to be a better way. So we built the tool we always wished we had.
              </p>
              <p className="text-slate-600 mb-6">
                Every feature in this platform reflects a real pain point we've lived through: missed time-bar deadlines, notices lost in email threads, claims rejected for poor documentation, and hours wasted on manual reporting. We designed Gantt to eliminate those problems — with AI-powered compliance checks, automated deadline tracking, and a contract rules engine that adapts to JBCC, FIDIC, NEC3, and NEC4.
              </p>
              <p className="text-slate-600 mb-8">
                Our construction planning background means we don't just understand the software — we understand critical path methodology, delay fragnets, float analysis, and the contractual nuances that determine whether a claim succeeds or fails. That expertise is baked into every module.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="border-l-2 border-cyan-500 pl-4">
                  <h4 className="font-black text-slate-900 text-lg">Our Mission</h4>
                  <p className="text-slate-600 text-sm mt-1">Give contractors the same level of contractual intelligence that was previously only available to large QS firms — accessible, automated, and always compliant.</p>
                </div>
                <div className="border-l-2 border-cyan-500 pl-4">
                  <h4 className="font-black text-slate-900 text-lg">Why Us</h4>
                  <p className="text-slate-600 text-sm mt-1">We've sat in the same site meetings, fought the same claim disputes, and tracked the same programmes. This software solves our own problems first.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Shield className="h-8 w-8 text-cyan-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-1">Contract Expertise</h4>
                  <p className="text-slate-500 text-sm">JBCC, FIDIC, NEC3 & NEC4 built into the engine</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Zap className="h-8 w-8 text-cyan-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-1">AI-Powered</h4>
                  <p className="text-slate-500 text-sm">Automated reports, compliance checks, and analytics</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Clock className="h-8 w-8 text-cyan-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-1">Time-Bar Alerts</h4>
                  <p className="text-slate-500 text-sm">Never miss a contractual deadline again</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Hammer className="h-8 w-8 text-cyan-600 mx-auto mb-3" />
                  <h4 className="font-bold text-slate-900 mb-1">Site-Tested</h4>
                  <p className="text-slate-500 text-sm">Every feature validated on real projects</p>
                </div>
              </div>
              <div className="bg-slate-900 rounded-lg p-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-black text-white"><AnimatedCounter target={15} suffix="+" /></p>
                    <p className="text-slate-400 text-xs mt-1">Years in Construction</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white"><AnimatedCounter target={27} suffix="+" /></p>
                    <p className="text-slate-400 text-xs mt-1">Integrated Modules</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white"><AnimatedCounter target={4} /></p>
                    <p className="text-slate-400 text-xs mt-1">Contract Standards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-bold tracking-widest text-cyan-600 uppercase mb-3">Careers</span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Join Our Growing Team
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We're expanding across South Africa and looking for driven individuals who share our passion for construction technology. Be part of a team that's transforming how the industry manages projects.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            {careerRoles.map((role, index) => (
              <Card key={index} className="border-slate-200 hover:border-cyan-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <Building2 className="h-5 w-5 text-cyan-600" />
                    </div>
                    <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded">Open</span>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">{role.title}</h3>
                  <p className="text-sm text-slate-500 mb-3">{role.location} &middot; {role.type}</p>
                  <p className="text-slate-600 text-sm mb-4">{role.description}</p>
                  <ul className="space-y-2 text-sm text-slate-600 mb-4">
                    {role.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {req}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
                    onClick={() => scrollToSection('contact')}
                  >
                    Apply Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-2xl font-black text-white mb-3">Don't See Your Role?</h3>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              We're always interested in hearing from talented people in the construction and tech space. Send us your CV and tell us how you'd contribute to our mission.
            </p>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-white hover:bg-slate-100 text-slate-900 font-bold px-8"
            >
              Send Your CV <Mail className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Ready to Stop Losing Money on Missed Claims?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Join hundreds of contractors who have transformed their claims management process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isLoggedIn ? (
              <Button 
                size="lg"
                onClick={() => navigate('/dashboard')}
                className="bg-white hover:bg-slate-100 text-slate-900 font-bold text-lg px-8"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <>
                <Button 
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-white hover:bg-slate-100 text-slate-900 font-bold text-lg px-8"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('contact')}
                  className="border-slate-600 text-white hover:bg-slate-800 font-bold text-lg px-8"
                >
                  Schedule a Demo
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/gantt-logo-white.png" 
                alt="Gantt ACC" 
                className="h-8 w-auto mb-2"
              />
              <p className="text-cyan-400/70 text-[10px] tracking-[0.25em] uppercase font-semibold mb-3">
                Artificial Construction Cloud
              </p>
              <p className="text-slate-400 text-sm">
                AI-powered construction management software for JBCC-compliant delay claims.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('features')} className="text-slate-400 hover:text-white text-sm">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="text-slate-400 hover:text-white text-sm">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-slate-400 hover:text-white text-sm">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-white text-sm">Contact</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-slate-400 hover:text-white text-sm">About Us</button></li>
                <li><button onClick={() => scrollToSection('careers')} className="text-slate-400 hover:text-white text-sm">Careers</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/popia" className="text-slate-400 hover:text-white text-sm">POPIA Compliance</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Gantt ACC — Artificial Construction Cloud. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
